//
// Copies text that may still be loading when the user clicks (MEL-574: the diagnosis prompt is
// fetched from /diagnose/context on demand). The clipboard write MUST be initiated synchronously
// inside the click's user gesture — if we `await` the fetch first and then call
// `navigator.clipboard.writeText`, the browser has lost the user-activation and silently rejects
// the write (nothing gets copied). Instead we hand `navigator.clipboard.write` a ClipboardItem
// whose value is a Promise, so the write is authorised now and resolves once the text arrives.
//
// Falls back to `writeText` where the async ClipboardItem form is unsupported (older browsers) —
// there the late-resolving case can still fail, but the common case (text already resolved) works.
//
export const copyTextAsync = (textPromise: Promise<string>): Promise<void> => {
  const w = window as unknown as { ClipboardItem?: typeof ClipboardItem }

  if (navigator.clipboard && typeof navigator.clipboard.write === 'function' && typeof w.ClipboardItem === 'function') {
    try {
      const item = new w.ClipboardItem({
        'text/plain': textPromise.then(text => new Blob([text ?? ''], { type: 'text/plain' })),
      })
      return navigator.clipboard.write([item])
    } catch {
      // some browsers expose ClipboardItem but reject a Promise value at construction —
      // fall through to the writeText path
    }
  }

  return textPromise.then(text => navigator.clipboard.writeText(text ?? ''))
}
